import { defineStore } from 'pinia';
import api from '@/services/api';
import { mapArrayToObjectsByKey } from '@/helpers/functions';
import router from '@/router';
import userLocalStorage from '@/services/userLocalStorage';
import abilities from '@/services/abilities';

export const useUserStore = defineStore('user', {
  state: () => ({
    loading: false,
    user: {},
    workspace: {},
    workspaces: [],
    organizations: [],
    abilitiesByOrganization: {},
    authorizationByOrganization: {},
    gallery: {
      images: [],
      touched: false
    },
    loaded: false,
  }),
  getters: {
    workspacesById: (state) => mapArrayToObjectsByKey(state.workspaces, 'id'),
    organizationsById: (state) => mapArrayToObjectsByKey(state.organizations, 'id'),
    organizationWorkspacesById: (state) => {
      const organizations = {};
      Object.keys(state.organizationsById).forEach((key) => { organizations[key] = []; });
      state.workspaces.forEach((workspace) => {
        const { organizationId } = workspace;
        organizations[organizationId].push(workspace);
      });
      return organizations;
    },
  },
  actions: {
    async getUser(force) {
      if (!this.loaded || force) {
        try {
          const {
            user, workspaces, organizations, abilitiesByOrganization, authorizationByOrganization
          } = await api.user.me();
          this.user = user;
          this.workspaces = workspaces;
          this.organizations = organizations;
          this.abilitiesByOrganization = abilitiesByOrganization;
          this.authorizationByOrganization = authorizationByOrganization;
          this.setWorkspaceByDefault();
          abilities.setAbilities({ abilitiesByOrganization: this.abilitiesByOrganization, organizationId: this.workspace.organizationId });
          this.loaded = true;
        } catch (err) {
          console.log(err);
        }
      }
    },
    updateUser({ user }) {
      this.user = user;
    },
    setWorkspaceByDefault() {
      const { currentRoute: { params: { workspaceId: paramsWorkspaceId } } } = router;
      let workspace = this.workspacesById[Number(paramsWorkspaceId)] || this.workspacesById[userLocalStorage.getSettings('workspaceId')];
      if (!workspace) {
        const organizationsEntries = Object.entries(this.authorizationByOrganization);
        const ownerOrg = organizationsEntries.find(([, org]) => org.is_owner);
        const ownerOrgDefaultWorkspace = this.workspaces.find((workspace) => workspace.organizationId === Number(ownerOrg[0]) && workspace.default);
        const activeOrg = organizationsEntries.find(([, org]) => org.is_organization_active);
        if (activeOrg && !ownerOrg[1].is_organization_active) {
          const activeOrgDefaultWorkspaceId = activeOrg[1].assigned_workspace_ids[0];
          if (activeOrgDefaultWorkspaceId) workspace = this.workspacesById[activeOrgDefaultWorkspaceId];
        } else workspace = ownerOrgDefaultWorkspace;
      }
      this.workspace = workspace;
      userLocalStorage.setSettings('workspaceId', workspace.id);
    },
    async createWorkspace(payload) {
      this.workspaces.unshift(payload);
    },
    async updateWorkspace(payload) {
      const index = this.workspaces.findIndex((item) => item.id === payload.id);
      if (index > -1) this.workspaces.splice(index, 1, payload);
    },
    async deleteWorkspace(id) {
      const index = this.workspaces.findIndex((item) => item.id === id);
      if (index > -1) this.workspaces.splice(index, 1);
    },
    async updateOrganization(payload) {
      const index = this.organizations.findIndex((item) => item.id === payload.id);
      if (index > -1) this.organizations.splice(index, 1, payload);
    },
    clearStore() {
      this.$reset();
    },
  },
});
